import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Send, Loader2, Users, MessageCircle } from 'lucide-react';

const MessagesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchConversations();
    fetchUsers();
    
    // Rafraîchir les messages toutes les 5 secondes
    const interval = setInterval(() => {
      if (selectedConversation) {
        fetchMessages(selectedConversation.id);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [user, navigate, selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const res = await api.get('/messages/conversations');
      setConversations(res.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users/all');
      setUsers(res.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const res = await api.get(`/messages/conversation/${conversationId}`);
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
    setShowNewChat(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await api.post('/messages/send', {
        receiver_id: selectedConversation.other_user_id,
        content: newMessage
      });
      
      setNewMessage('');
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      toast.error('Erreur d\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const handleStartNewChat = async (userId) => {
    try {
      // Créer une nouvelle conversation
      const res = await api.post('/messages/start-conversation', {
        receiver_id: userId
      });
      
      setSelectedConversation(res.data.conversation);
      fetchConversations();
      setShowNewChat(false);
      setMessages([]);
    } catch (error) {
      toast.error('Erreur de création de conversation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#E50914]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <Header />
      
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            💬 Messages
          </h1>
          <p className="text-zinc-400">
            Échangez avec l'Admin et les autres utilisateurs
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[600px]">
          {/* Liste des conversations */}
          <Card className="glass border-zinc-800 lg:col-span-1">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users size={20} />
                  Conversations
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => setShowNewChat(!showNewChat)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  + Nouveau
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto h-[480px]">
              {showNewChat ? (
                <div className="space-y-2">
                  <p className="text-zinc-400 text-sm mb-3">Démarrer une conversation avec :</p>
                  {users
                    .filter(u => u.id !== user.id)
                    .map(u => (
                      <button
                        key={u.id}
                        onClick={() => handleStartNewChat(u.id)}
                        className="w-full p-3 bg-zinc-900 hover:bg-zinc-800 rounded-lg text-left transition-colors"
                      >
                        <p className="text-white font-semibold">{u.full_name}</p>
                        <p className="text-xs text-zinc-500">{u.phone_number}</p>
                      </button>
                    ))}
                </div>
              ) : conversations.length === 0 ? (
                <p className="text-center text-zinc-400 py-8">
                  Aucune conversation
                </p>
              ) : (
                conversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    className={`w-full p-3 rounded-lg mb-2 text-left transition-colors ${
                      selectedConversation?.id === conv.id
                        ? 'bg-[#E50914]'
                        : 'bg-zinc-900 hover:bg-zinc-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-white font-semibold">{conv.other_user_name}</p>
                      {conv.unread_count > 0 && (
                        <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-400 truncate">{conv.last_message}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {new Date(conv.last_message_at).toLocaleString('fr-FR')}
                    </p>
                  </button>
                ))
              )}
            </CardContent>
          </Card>

          {/* Zone de chat */}
          <Card className="glass border-zinc-800 lg:col-span-2 flex flex-col">
            {selectedConversation ? (
              <>
                <CardHeader className="border-b border-zinc-800">
                  <CardTitle className="flex items-center gap-3">
                    <MessageCircle size={20} />
                    {selectedConversation.other_user_name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 ? (
                    <p className="text-center text-zinc-400 py-8">
                      Aucun message. Commencez la conversation !
                    </p>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            msg.sender_id === user.id
                              ? 'bg-[#E50914] text-white'
                              : 'bg-zinc-800 text-white'
                          }`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <p className="text-xs opacity-70 mt-1">
                            {new Date(msg.created_at).toLocaleTimeString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </CardContent>
                <div className="p-4 border-t border-zinc-800">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Tapez votre message..."
                      className="flex-1 bg-zinc-900 border-zinc-800 text-white"
                    />
                    <Button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="bg-[#E50914] hover:bg-[#F8212C]"
                    >
                      {sending ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-zinc-400">
                  <MessageCircle size={64} className="mx-auto mb-4 opacity-50" />
                  <p>Sélectionnez une conversation pour commencer</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
};

export default MessagesPage;
